import { BugIcon, FeedbackLightBulbIcon, LightningIcon } from "@xola/icons";
import { Button, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import React, { HTMLAttributes, PropsWithChildren, useLayoutEffect, useRef, useState } from "react";

const feedbackButton = clsx(
    "hover:bg-white hover:bg-opacity-100 hover:text-black",
    "active:bg-opacity-80 active:bg-white active:text-black",
    "cursor-pointer",
);
const featureButton = "bg-white bg-opacity-10";
const bugButton = "bg-red-dark";

const ButtonTooltip = ({
    children,
    className,
    ...rest
}: PropsWithChildren<{ content: React.ReactNode; className?: string }>) => (
    <Tooltip placement="right" className={clsx("max-w-fit", className)} {...rest}>
        {children}
    </Tooltip>
);

const FeatureButton = ({
    showText,
    ...rest
}: HTMLAttributes<HTMLButtonElement> & {
    showText: boolean;
}) => {
    return (
        <ButtonTooltip content="Product feedback, enhancements, and feature requests">
            <Button as="div" className={clsx(feedbackButton, featureButton)} size="small" {...rest}>
                <div className="px-1 xl:px-0">
                    <FeedbackLightBulbIcon size="medium" />
                </div>
                {showText && <span className="ml-2 text-sm">Feedback</span>}
            </Button>
        </ButtonTooltip>
    );
};

const BugButton = ({
    showIcon,
    ...rest
}: HTMLAttributes<HTMLButtonElement> & {
    showIcon: boolean;
}) => {
    return (
        <ButtonTooltip content="Quick Bug Submission">
            <Button as="div" className={clsx(feedbackButton, bugButton)} size="small" {...rest}>
                {showIcon && (
                    <div className="px-1 xl:px-0">
                        <LightningIcon size="medium" />
                    </div>
                )}
                <BugIcon size="medium" />
            </Button>
        </ButtonTooltip>
    );
};

interface FeedbackButtonProps {
    className?: string;
    onFeatureClick: () => void;
    onBugClick: () => void;
}

const FeedbackButton = ({ className, onFeatureClick, onBugClick }: FeedbackButtonProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [showText, setShowText] = useState(false);
    const [showIcon, setShowIcon] = useState(true);

    useLayoutEffect(() => {
        const updateVisibility = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setShowText(containerWidth > 180);
                setShowIcon(containerWidth > 130);
            }
        };

        updateVisibility();
        const resizeObserver = new ResizeObserver(updateVisibility);

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    return (
        <div
            ref={containerRef}
            className={clsx("flex flex-row flex-wrap items-center justify-center gap-2", className)}
        >
            <FeatureButton showText={showText} onClick={onFeatureClick} />
            <BugButton showIcon={showIcon} onClick={onBugClick} />
        </div>
    );
};

export default FeedbackButton;
